import clsx from 'clsx';
import {useEffect} from 'react';
import {Trans} from '../i18n/trans';
import {SiteHeader} from '../ui/navigation/site-header';
import {useForm} from 'react-hook-form';
import {
  ContactPagePayload,
  useSubmitContactForm,
} from './use-submit-contact-form';
import {useRecaptcha} from '../recaptcha/use-recaptcha';
import {StaticPageTitle} from '../seo/static-page-title';
import {useAuth} from '../auth/use-auth';

const CONTACT_MESSAGE_MAX = 1000;

const contactInputClass = clsx(
  'h-[65px] w-full rounded-[24px] border-2 bg-[#f4f4f4] px-[18px] py-[22px]',
  'text-[16px] leading-[21px] text-[#535353] placeholder:text-[16px] placeholder:leading-[21px] placeholder:text-[#989898]',
  'focus:outline-none dark:bg-[#1d2128] dark:text-[#f3f4f6] dark:placeholder:text-[#7d8794]',
);

const contactTextareaClass = clsx(
  'h-[214px] w-full resize-none border-0 bg-transparent p-0',
  'text-[16px] leading-[21px] text-[#535353] placeholder:text-[16px] placeholder:leading-[21px] placeholder:text-[#989898]',
  'focus:outline-none dark:text-[#f3f4f6] dark:placeholder:text-[#7d8794]',
);

export function ContactUsPageNew() {
  const {user} = useAuth();
  const form = useForm<ContactPagePayload>({
    defaultValues: {
      email: user?.email ?? '',
    },
  });
  const submitForm = useSubmitContactForm(form);
  const {verify, isVerifying} = useRecaptcha('contact');
  const messageValue = form.watch('message') || '';
  const {
    register,
    setValue,
    getValues,
    formState: {errors},
  } = form;

  useEffect(() => {
    if (user?.email && !getValues('email')) {
      setValue('email', user.email);
    }
  }, [user?.email, getValues, setValue]);

  return (
    <div className="min-h-full bg-[#f5f5f7] dark:bg-[#14161a]">
      <StaticPageTitle>
        <Trans message="Contact us" />
      </StaticPageTitle>
      <SiteHeader />
      <div className="mx-auto flex w-full max-w-[1320px] px-4 py-8 sm:px-6 sm:py-12 lg:px-8 lg:py-[72px]">
        <section className="w-full rounded-[44px] border-2 border-[#e9ecf0] bg-white px-6 py-8 dark:border-[#2a2d33] dark:bg-[#232832] sm:px-10 sm:py-10 lg:min-h-[709px] lg:rounded-[60px] lg:px-[50px] lg:py-[42px]">
          <h1 className="text-[20px] font-bold leading-[1.45] text-[#d8562a]">
            <Trans message="Contact us" />
          </h1>
          <form
            onSubmit={form.handleSubmit(async value => {
              const isValid = await verify();
              if (isValid) {
                submitForm.mutate(value);
              }
            })}
            className="mx-auto mt-8 flex w-full max-w-[554px] flex-col gap-4 lg:mt-10"
          >
            <div>
              <input
                {...register('name', {
                  required: 'Name is required.',
                })}
                type="text"
                placeholder="Name"
                aria-invalid={errors.name ? 'true' : 'false'}
                className={clsx(
                  contactInputClass,
                  errors.name
                    ? 'border-[#d8562a]'
                    : 'border-transparent focus:border-[#2b7e20]',
                )}
              />
              {errors.name && (
                <p className="mt-2 px-1 text-sm text-[#d8562a]">
                  {errors.name.message}
                </p>
              )}
            </div>
            <div>
              <input
                {...register('email', {
                  required: 'Email is required.',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address.',
                  },
                })}
                type="email"
                placeholder="Email"
                aria-invalid={errors.email ? 'true' : 'false'}
                className={clsx(
                  contactInputClass,
                  errors.email
                    ? 'border-[#d8562a]'
                    : 'border-transparent focus:border-[#2b7e20]',
                )}
              />
              {errors.email && (
                <p className="mt-2 px-1 text-sm text-[#d8562a]">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div
              className={clsx(
                'rounded-[24px] border-2 bg-[#f4f4f4] px-[18px] pb-[10px] pt-[14px] dark:bg-[#1d2128]',
                errors.message ? 'border-[#d8562a]' : 'border-transparent',
                !errors.message && 'focus-within:border-[#2b7e20]',
              )}
            >
              <textarea
                {...register('message', {
                  required: 'Message is required.',
                  maxLength: {
                    value: CONTACT_MESSAGE_MAX,
                    message: `Message cannot be more than ${CONTACT_MESSAGE_MAX} characters.`,
                  },
                })}
                placeholder="Message"
                rows={8}
                maxLength={CONTACT_MESSAGE_MAX}
                aria-invalid={errors.message ? 'true' : 'false'}
                className={contactTextareaClass}
              />
              <p className="text-right text-[16px] text-[#535353] dark:text-[#b3bcc8]">
                {messageValue.length}/{CONTACT_MESSAGE_MAX} characters
              </p>
            </div>
            {errors.message && (
              <p className="mt-[-8px] px-1 text-sm text-[#d8562a]">
                {errors.message.message}
              </p>
            )}
            <button
              type="submit"
              disabled={submitForm.isLoading || isVerifying}
              className="mx-auto mt-2 h-[65px] w-[141px] rounded-[24px] bg-[#2b7e20] text-[16px] font-normal text-white transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <Trans message="Send" />
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
